import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";

/**
 * One live region for the whole app (design.md §10): status changes are
 * announced once, through here, and never carried by colour or position
 * alone. `announce()` from anywhere; the region is rendered once in the
 * layout. Two alternating nodes so an identical message twice in a row is
 * still spoken (a live region ignores an unchanged string).
 */
interface Announcer {
  announce: (message: string, politeness?: "polite" | "assertive") => void;
}

const AnnouncerContext = createContext<Announcer>({ announce: () => {} });

export function useAnnounce(): Announcer["announce"] {
  return useContext(AnnouncerContext).announce;
}

export function AnnouncerProvider({ children }: { children: ReactNode }) {
  const [slots, setSlots] = useState<[string, string]>(["", ""]);
  const [assertive, setAssertive] = useState("");
  const flip = useRef(0);
  const announce = useCallback<Announcer["announce"]>((message, politeness = "polite") => {
    if (politeness === "assertive") {
      setAssertive("");
      // Next tick so the same text read twice still changes the node.
      setTimeout(() => setAssertive(message), 0);
      return;
    }
    flip.current = 1 - flip.current;
    const i = flip.current;
    setSlots(i === 0 ? [message, ""] : ["", message]);
  }, []);
  const value = useMemo(() => ({ announce }), [announce]);
  return (
    <AnnouncerContext.Provider value={value}>
      {children}
      <div className="sr-only" aria-live="polite" aria-atomic="true" data-testid="live-region">
        <span>{slots[0]}</span>
        <span>{slots[1]}</span>
      </div>
      <div className="sr-only" role="alert" aria-atomic="true" data-testid="alert-region">
        {assertive}
      </div>
    </AnnouncerContext.Provider>
  );
}
